import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import {
  FaUsers,
  FaUserShield,
  FaShoppingCart,
  FaBox,
  FaGraduationCap,
  FaBook,
  FaChalkboardTeacher,
  FaMapMarkerAlt,
  FaBuilding,
  FaEnvelope,
  FaChartLine,
  FaTrophy,
  FaShieldVirus,
  FaFileExcel
} from "react-icons/fa";
import { CiBarcode } from "react-icons/ci";
import { toast } from "react-toastify";
import { fetchDashboardData } from "../../redux/Slices/DashboardSlice";
import { useTitle } from "../../context/TitleContext";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import axios from "axios";
import { fetchAllGrades, fetchAllSections, fetchAllProducts } from "../../redux/Slices/ProductSlice";

import i18n from "../../i18n/i18n";

const BASE_URL = process.env.REACT_APP_BASE_URL || "https://backend.mystore.education/api";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const Home = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const [loading, setLoading] = useState(true);
  const [loadingExportOrders, setLoadingExportOrders] = useState(false);
  const [loadingExportStudents, setLoadingExportStudents] = useState(false);
  const [loadingExportProductStudents, setLoadingExportProductStudents] = useState(false);
  const [loadingExportProducts, setLoadingExportProducts] = useState(false);

  const { dashboardData, error } = useSelector((state) => state.dashboard);
  const {
    grades,
    sections,
    allProducts,
    meta,
    allProductsLoading
  } = useSelector((state) => state.products);
  useEffect(() => {
    setTitle("الرئيسية");
  }, [setTitle]);
  useEffect(() => {
    dispatch(fetchAllGrades());
    dispatch(fetchAllSections());
    dispatch(fetchAllProducts());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, i18n.language]);
 
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(fetchAllProducts(currentPage));
  }, [dispatch, currentPage]);

  const handleNext = () => {
    if (meta.current_page < meta.last_page) {
      setCurrentPage(meta.current_page + 1);
    }
  };

  const handlePrev = () => {
    if (meta.current_page > 1) {
      setCurrentPage(meta.current_page - 1);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await dispatch(fetchDashboardData());
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        toast.error("خطأ في تحميل البيانات");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "50vh" }}
      >
        <div className="spinner-border text-danger" role="status">
          <span className="visually-hidden">جاري التحميل...</span>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="text-center py-5">
        <h5 className="text-muted">لا توجد بيانات متاحة</h5>
      </div>
    );
  }

  const data = dashboardData.data;

  // Statistics Cards
  const statsCards = [
    {
      title: "المستخدمين",
      value: data.users_count,
      icon: <FaUsers />,
      color: "primary",
      link: "/users",
    },
    {
      title: "الأدمن",
      value: data.admins_count,
      icon: <FaUserShield />,
      color: "success",
      link: "/admins",
    },
    {
      title: "المنتجات",
      value: data.products_count,
      icon: <FaBox />,
      color: "info",
      link: "/products",
    },
    {
      title: "التصنيفات",
      value: data.categories_count,
      icon: <FaBook />,
      color: "warning",
      link: "/category",
    },
    {
      title: "الصفوف",
      value: data.grades_count,
      icon: <FaGraduationCap />,
      color: "secondary",
      link: "/grades",
    },
    {
      title: "المواد",
      value: data.subjects_count,
      icon: <FaBook />,
      color: "dark",
      link: "/study-materials",
    },
    {
      title: "المعلمين",
      value: data.teachers_count,
      icon: <FaChalkboardTeacher />,
      color: "danger",
      link: "/teachers",
    },
    {
      title: "المدن",
      value: data.cities_count,
      icon: <FaBuilding />,
      color: "primary",
      link: "/cities",
    },
    {
      title: "المناطق",
      value: data.regions_count,
      icon: <FaMapMarkerAlt />,
      color: "success",
      link: "/regions",
    },
    {
      title: "الطلبات",
      value: data.orders_count,
      icon: <FaShoppingCart />,
      color: "info",
      link: "/orders",
    },
    {
      title: "عربات التسوق",
      value: data.carts_count,
      icon: <FaShoppingCart />,
      color: "warning",
      link: "/orders",
    },
    {
      title: "عناصر العربات",
      value: data.cart_items_count,
      icon: <FaBox />,
      color: "secondary",
      link: "/orders",
    },
    {
      title: "أكواد المعلمين",
      value: data.teachers_codes_count,
      icon: <CiBarcode />,
      color: "primary",
      link: "/teachers_codes",
    },
    {
      title: " الشروط والاحكام",
      value: "—", // 🔧 FIX: الباك مش بيرجع قيمة عدد للـ terms في dashboard
      icon: <FaShieldVirus />,
      color: "primary",
      link: "/terms",
    },
    {
      title: " سياسة الخصوصية",
      value: "—", // 🔧 FIX: الباك مش بيرجع قيمة عدد للـ privacy في dashboard
      icon: <CiBarcode />,
      color: "primary",
      link: "/privacy",
    },
    {
      title: "رسائل التواصل",
      value: data.contacts_count,
      icon: <FaEnvelope />,
      color: "dark",
      link: "/contacts",
    },
  ];

  const StatCard = ({ title, value, icon, color, link }) => (
    <motion.div
      className="col-lg-3 col-md-4 col-sm-6 mb-4"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <div
        className={`card border-0 shadow-sm h-100 cursor-pointer`}
        onClick={() => navigate(link)}
        style={{ cursor: 'pointer' }}
      >
        <div className="card-body text-center">
          <div className={`text-${color} mb-3`} style={{ fontSize: "2.5rem" }}>
            {icon}
          </div>
          <h3 className={`text-${color} mb-1`}>{value}</h3>
          <p className="text-muted mb-0">{title}</p>
        </div>
      </div>
    </motion.div>
  );

  const OrderStatusCard = ({ title, data, color }) => (
    <div className="col-md-4 mb-4">
      <div className="card border-0 shadow-sm h-100">
        <div className="card-body text-center">
          <h5 className="card-title">{title}</h5>
          <div className="mt-3">
            {Object.entries(data).map(([status, count]) => (
              <div key={status} className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-capitalize">
                  {status === 'pending' ? 'في الانتظار' :
                    status === 'in_progress' ? 'قيد التنفيذ' :
                      status === 'in_way' ? 'في الطريق' :
                        status === 'delivered' ? 'تم التسليم' :
                          status === 'cancelled' ? 'ملغي' :
                            status === 'not_paid' ? 'غير مدفوع' :
                              status === 'paid' ? 'مدفوع' :
                                status === 'cash' ? 'نقدي' :
                                  status === 'electronic' ? 'إلكتروني' :
                                    status}
                </span>
                <span className={`badge bg-${color} text-white`}>{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const LatestItemsCard = ({ title, items, type }) => (
    <div className="col-md-6 mb-4">
      <div className="card border-0 shadow-sm h-100">
        <div className="card-header bg-white border-0">
          <h5 className="card-title mb-0">{title}</h5>
        </div>
        <div className="card-body">
          <div className="list-group list-group-flush">
            {items?.slice(0, 5).map((item, index) => (
              <div key={index} className="list-group-item border-0 px-0 py-2">
                <div className="d-flex align-items-center">
                  {type === 'users' && (
                    <img
                      src={item.image}
                      alt={item.name}
                      className="rounded-circle me-3"
                      style={{ width: "40px", height: "40px", objectFit: "cover" }}
                    />
                  )}
                  <div className="flex-grow-1">
                    <h6 className="mb-1">
                      {type === 'users' ? item.name :
                        type === 'products' ? item.title_ar :
                          type === 'categories' ? item.name_ar :
                            type === 'orders' ? `#${item.order_number?.substring(0, 8)}...` :
                              type === 'carts' ? `عربة #${item.id}` :
                                type === 'contacts' ? item.name :
                                  item.name || item.title}
                    </h6>
                    <small className="text-muted">
                      {type === 'users' ? item.phone :
                        type === 'products' ? `${item.real_price} ج.م` :
                          type === 'categories' ? (item.status === '1' ? 'نشط' : 'غير نشط') :
                            type === 'orders' ? `${item.total_price_after_discount} ج.م` :
                              type === 'carts' ? `المستخدم #${item.user_id}` :
                                type === 'contacts' ? item.subject :
                                  ''}
                    </small>
                  </div>
                  <small className="text-muted">
                    {new Date(item.created_at).toLocaleDateString('ar-EG')}
                  </small>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // دالة مساعدة للحصول على التوكن — تجرب كل المسارات الممكنة
  const getAuthToken = () => {
    try {
      // Check localStorage first
      const localData = localStorage.getItem("loggedUser");
      if (localData) {
        const parsed = JSON.parse(localData);
        if (parsed.token) return parsed.token;
        if (parsed.data?.token) return parsed.data.token;
        if (parsed.result?.token) return parsed.result.token;
        if (parsed.user?.token) return parsed.user.token;
      }

      // Check sessionStorage
      const sessionData = sessionStorage.getItem("user") || sessionStorage.getItem("loggedUser");
      if (sessionData) {
        const parsed = JSON.parse(sessionData);
        if (parsed.token) return parsed.token;
        if (parsed.data?.token) return parsed.data.token;
        if (parsed.result?.token) return parsed.result.token;
        if (parsed.user?.token) return parsed.user.token;
      }

      return null;
    } catch {
      return null;
    }
  };

  // export file
  const handleExportOrders = async () => {
    const date_from = document.getElementById("dateFrom").value;
    const date_to = document.getElementById("dateTo").value;
    const payment_status = document.getElementById("paymentStatus").value;
    const order_status = document.getElementById("orderStatus").value;

    try {
      setLoadingExportOrders(true);
      const token = getAuthToken();
      if (!token) {
        toast.error("خطأ في المصادقة، يرجى تسجيل الدخول مجدداً");
        setLoadingExportOrders(false);
        return;
      }

      const response = await axios.get(
        `${BASE_URL}/dashboard/exports/orders`,
        {
          params: { date_from, date_to, payment_status, order_status },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept-Language": "ar",
          },
        }
      );

      if (response.data?.status && response.data.data?.download_url) {
        const downloadUrl = response.data.data.download_url;
        document.querySelector("#exportOrdersModal [data-bs-dismiss='modal']")?.click();
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute("download", "orders.xlsx");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("تم إنشاء ملف Excel بنجاح");
      } else {
        toast.warn(response.data?.message || "لم يتم العثور على رابط التحميل.");
      }
    } catch (error) {
      console.error(error);
      toast.error("حدث خطأ أثناء إنشاء الملف.");
    }
    finally {
      setLoadingExportOrders(false);
    }
  };
  const handleExportStudents = async () => {
    const grade_id = document.getElementById("grade_id").value;
    const section_id = document.getElementById("section_id").value;

    try {
      setLoadingExportStudents(true);
      const token = getAuthToken();
      if (!token) {
        toast.error("خطأ في المصادقة، يرجى تسجيل الدخول مجدداً");
        setLoadingExportStudents(false);
        return;
      }

      const response = await axios.get(
        `${BASE_URL}/dashboard/exports/students`,
        {
          params: { grade_id, section_id },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept-Language": "ar",
          },
        }
      );

      if (response.data?.status && response.data.data?.download_url) {
        const downloadUrl = response.data.data.download_url;
        document.querySelector("#exportStudentsModal [data-bs-dismiss='modal']")?.click();
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute("download", "students.xlsx");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("تم إنشاء ملف Excel بنجاح");
      } else {
        toast.warn(response.data?.message || "لم يتم العثور على رابط التحميل.");
      }
    } catch (error) {
      console.error(error);
      toast.error("حدث خطأ أثناء إنشاء الملف.");
    }
    finally {
      setLoadingExportStudents(false);
    }
  };
  const handleExportProductStudent = async () => {
    const product_id = document.getElementById("product_id").value;

    if (!product_id) {
      toast.warn("يرجى اختيار منتج أولاً");
      return;
    }
    try {
      setLoadingExportProductStudents(true);
      const token = getAuthToken();
      if (!token) {
        toast.error("خطأ في المصادقة، يرجى تسجيل الدخول مجدداً");
        setLoadingExportProductStudents(false);
        return;
      }

      const response = await axios.get(
        `${BASE_URL}/dashboard/exports/product-students/${product_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept-Language": "ar",
          },
        }
      );

      if (response.data?.status && response.data.data?.download_url) {
        const downloadUrl = response.data.data.download_url;
        document.querySelector("#exportProductStudentsModal [data-bs-dismiss='modal']")?.click();
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute("download", "students_by_products.xlsx");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("تم إنشاء ملف Excel بنجاح");
      } else {
        toast.warn(response.data?.message || "لم يتم العثور على رابط التحميل.");
      }
    } catch (error) {
      console.error(error);
      toast.error("حدث خطأ أثناء إنشاء الملف.");
    }
    finally {
      setLoadingExportProductStudents(false);
    }
  };
  const handleExportProducts = async () => {
    try {
      setLoadingExportProducts(true);
      const token = getAuthToken();
      if (!token) {
        toast.error("خطأ في المصادقة، يرجى تسجيل الدخول مجدداً");
        setLoadingExportProducts(false);
        return;
      }

      const response = await axios.get(
        `${BASE_URL}/dashboard/export/products`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept-Language": "ar",
          },
        }
      );

      if (response.data?.status && response.data.data?.download_url) {
        const downloadUrl = response.data.data.download_url;
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute("download", "products.xlsx");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("تم إنشاء ملف Excel بنجاح");
      } else {
        toast.warn(response.data?.message || "لم يتم العثور على رابط التحميل.");
      }
    } catch (error) {
      console.error(error);
      toast.error("حدث خطأ أثناء إنشاء الملف.");
    }
    finally {
      setLoadingExportProducts(false);
    }
  };

  return (
    <div className="container-fluid">
      {/* Welcome Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5">
              <h1 className="display-4 text-danger mb-3">مرحباً بك في لوحة التحكم</h1>
              <p className="lead text-muted">إدارة شاملة لمنصة ماي استور</p>
            </div>
          </div>
        </div>
      </div>


      <div className="row mb-5">
        <div className="col-12">
          <h4 className="mb-4">تصدير ملفات Excel</h4>
        </div>
        <div className="col-xl-3 col-lg-3 col-md-6 col-12">
          <div className="card shadow-sm rounded-2 p-3 text-center border-0 my-2">
            <h4 className="text-primary mt-3 mb-1" style={{ fontSize: "30px" }}><i className="bi bi-box-seam-fill"></i></h4>
            <b className="d-block my-2" style={{ fontSize: "12px" }}>تصدير الطلبات</b>
            <button
              className="btn btn-primary btn-sm shadow-sm rounded-5 border-0 mx-4 py-2"
              style={{ fontSize: "11px" }}
              data-bs-toggle="modal"
              data-bs-target="#exportOrdersModal"
            >
              <FaFileExcel /> تصدير ملف Excel
            </button>
          </div>
        </div>
        <div className="col-xl-3 col-lg-3 col-md-6 col-12">
          <div className="card shadow-sm rounded-2 p-3 text-center border-0 my-2">
            <h4 className="text-secondary mt-3 mb-1" style={{ fontSize: "30px" }}><i className="bi bi-mortarboard-fill"></i></h4>
            <b className="d-block my-2" style={{ fontSize: "12px" }}>تصدير الطلاب</b>
            <button
              className="btn btn-secondary btn-sm shadow-sm rounded-5 border-0 mx-4 py-2"
              style={{ fontSize: "11px" }}
              data-bs-toggle="modal"
              data-bs-target="#exportStudentsModal"
            >
              <FaFileExcel /> تصدير ملف Excel
            </button>
          </div>
        </div>
        <div className="col-xl-3 col-lg-3 col-md-6 col-12">
          <div className="card shadow-sm rounded-2 p-3 text-center border-0 my-2">
            <h4 className="text-success mt-3 mb-1" style={{ fontSize: "30px" }}><i className="bi bi-person"></i></h4>
            <b className="d-block my-2" style={{ fontSize: "12px" }}>تصدير الطلاب حسب المنتج</b>
            <button
              className="btn btn-success btn-sm shadow-sm rounded-5 border-0 mx-4 py-2"
              style={{ fontSize: "11px" }}
              data-bs-toggle="modal"
              data-bs-target="#exportProductStudentsModal"
            >
              <FaFileExcel /> تصدير ملف Excel
            </button>
          </div>
        </div>
        <div className="col-xl-3 col-lg-3 col-md-6 col-12">
          <div className="card shadow-sm rounded-2 p-3 text-center border-0 my-2">
            <h4 className="text-warning mt-3 mb-1" style={{ fontSize: "30px" }}><i className="bi bi-boxes"></i></h4>
            <b className="d-block my-2" style={{ fontSize: "12px" }}>تصدير المنتجات</b>
            <button
              type="submit"
              className="btn btn-warning btn-sm shadow-sm rounded-5 border-0 mx-4 py-2"
              style={{ fontSize: "11px" }}
              id="exportBtn"
              onClick={handleExportProducts}
              disabled={loadingExportProducts}
            >
              {loadingExportProducts ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  جاري التصدير...
                </>
              ) : (
                <>
                  <FaFileExcel /> تصدير ملف Excel
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      {/* Statistics Cards */}
      <div className="row mb-5">
       <div className="col-12">
          <h4 className="mb-4"> إحصائيات عامة </h4>
        </div>
        {statsCards.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>

      {/* Orders Status */}
      <div className="row mb-5">
        <div className="col-12">
          <h4 className="mb-4">إحصائيات الطلبات</h4>
        </div>
        <OrderStatusCard
          title="حالة الطلبات"
          data={data.orders_by_status}
          color="primary"
        />
        <OrderStatusCard
          title="نوع الدفع"
          data={data.orders_by_payment_type}
          color="success"
        />
        <OrderStatusCard
          title="حالة الدفع"
          data={data.orders_by_payment_status}
          color="warning"
        />
      </div>

      {/* Charts Section */}
      <div className="row mb-5">
        <div className="col-12">
          <h4 className="mb-4">الرسوم البيانية</h4>
        </div>

        {/* Sales Line Chart */}
        {data.sales_over_time && data.sales_over_time.length > 0 && (
          <div className="col-lg-8 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-0">
                <h5 className="card-title mb-0">
                  <FaChartLine className="me-2" />
                  المبيعات عبر الوقت
                </h5>
              </div>
              <div className="card-body">
                <Line
                  data={{
                    labels: data.sales_over_time.map(sale =>
                      new Date(sale.date).toLocaleDateString('ar-EG')
                    ),
                    datasets: [
                      {
                        label: 'المبيعات (ج.م)',
                        data: data.sales_over_time.map(sale => sale.total_sales),
                        borderColor: 'rgb(75, 192, 192)',
                        backgroundColor: 'rgba(75, 192, 192, 0.2)',
                        tension: 0.1,
                        fill: true,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top',
                      },
                      title: {
                        display: false,
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          callback: function (value) {
                            return value + ' ج.م';
                          }
                        }
                      },
                    },
                  }}
                  height={300}
                />
              </div>
            </div>
          </div>
        )}

        {/* Orders Status Doughnut Chart */}
        <div className="col-lg-4 mb-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white border-0">
              <h5 className="card-title mb-0">
                <FaShoppingCart className="me-2" />
                حالة الطلبات
              </h5>
            </div>
            <div className="card-body">
              <Doughnut
                data={{
                  labels: Object.keys(data.orders_by_status || {}).map(status =>
                    status === 'pending' ? 'في الانتظار' :
                      status === 'in_progress' ? 'قيد التنفيذ' :
                        status === 'in_way' ? 'في الطريق' :
                          status === 'delivered' ? 'تم التسليم' :
                            status === 'cancelled' ? 'ملغي' : status
                  ),
                  datasets: [
                    {
                      data: Object.values(data.orders_by_status || {}),
                      backgroundColor: [
                        '#FF6384',
                        '#36A2EB',
                        '#FFCE56',
                        '#4BC0C0',
                        '#9966FF',
                      ],
                      borderWidth: 2,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                    },
                  },
                }}
                height={300}
              />
            </div>
          </div>
        </div>

        {/* Top Products Bar Chart */}
        {data.top_products && data.top_products.length > 0 && (
          <div className="col-lg-6 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-0">
                <h5 className="card-title mb-0">
                  <FaTrophy className="me-2 text-warning" />
                  أفضل المنتجات
                </h5>
              </div>
              <div className="card-body">
                <Bar
                  data={{
                    labels: data.top_products.map(product => product.title_en),
                    datasets: [
                      {
                        label: 'عدد المبيعات',
                        data: data.top_products.map(product => product.total_sold),
                        backgroundColor: 'rgba(54, 162, 235, 0.6)',
                        borderColor: 'rgba(54, 162, 235, 1)',
                        borderWidth: 1,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          stepSize: 1,
                        }
                      },
                      x: {
                        ticks: {
                          maxRotation: 45,
                          minRotation: 45,
                        }
                      }
                    },
                  }}
                  height={300}
                />
              </div>
            </div>
          </div>
        )}

        {/* Top Categories Bar Chart */}
        {data.top_categories && data.top_categories.length > 0 && (
          <div className="col-lg-6 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-0">
                <h5 className="card-title mb-0">
                  <FaBook className="me-2 text-info" />
                  أفضل التصنيفات
                </h5>
              </div>
              <div className="card-body">
                <Bar
                  data={{
                    labels: data.top_categories.map(category => category.name_en),
                    datasets: [
                      {
                        label: 'عدد المنتجات',
                        data: data.top_categories.map(category => category.total_products),
                        backgroundColor: 'rgba(75, 192, 192, 0.6)',
                        borderColor: 'rgba(75, 192, 192, 1)',
                        borderWidth: 1,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          stepSize: 1,
                        }
                      },
                      x: {
                        ticks: {
                          maxRotation: 45,
                          minRotation: 45,
                        }
                      }
                    },
                  }}
                  height={300}
                />
              </div>
            </div>
          </div>
        )}

        {/* Payment Type Chart */}
        <div className="col-lg-6 mb-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white border-0">
              <h5 className="card-title mb-0">
                <FaShoppingCart className="me-2" />
                نوع الدفع
              </h5>
            </div>
            <div className="card-body">
              <Doughnut
                data={{
                  labels: Object.keys(data.orders_by_payment_type || {}).map(type =>
                    type === 'cash' ? 'نقدي' : 'إلكتروني'
                  ),
                  datasets: [
                    {
                      data: Object.values(data.orders_by_payment_type || {}),
                      backgroundColor: [
                        '#FF6384',
                        '#36A2EB',
                      ],
                      borderWidth: 2,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                    },
                  },
                }}
                height={300}
              />
            </div>
          </div>
        </div>

        {/* Payment Status Chart */}
        <div className="col-lg-6 mb-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white border-0">
              <h5 className="card-title mb-0">
                <FaEnvelope className="me-2" />
                حالة الدفع
              </h5>
            </div>
            <div className="card-body">
              <Doughnut
                data={{
                  labels: Object.keys(data.orders_by_payment_status || {}).map(status =>
                    status === 'paid' ? 'مدفوع' : 'غير مدفوع'
                  ),
                  datasets: [
                    {
                      data: Object.values(data.orders_by_payment_status || {}),
                      backgroundColor: [
                        '#4BC0C0',
                        '#FF6384',
                      ],
                      borderWidth: 2,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                    },
                  },
                }}
                height={300}
              />
            </div>
          </div>
        </div>
      </div>


      {/* Latest Items */}
      <div className="row mb-5">
        <div className="col-12">
          <h4 className="mb-4">أحدث الإضافات</h4>
        </div>
        <LatestItemsCard
          title="أحدث المستخدمين"
          items={data.latest_users}
          type="users"
        />
        <LatestItemsCard
          title="أحدث المنتجات"
          items={data.latest_products}
          type="products"
        />
        <LatestItemsCard
          title="أحدث التصنيفات"
          items={data.latest_categories}
          type="categories"
        />
        <LatestItemsCard
          title="أحدث الطلبات"
          items={data.latest_orders}
          type="orders"
        />
        <LatestItemsCard
          title="أحدث عربات التسوق"
          items={data.latest_carts}
          type="carts"
        />
        <LatestItemsCard
          title="أحدث رسائل التواصل"
          items={data.latest_contacts}
          type="contacts"
        />
      </div>

      {/* export orders modal */}
      <div
        className="modal fade"
        id="exportOrdersModal"
        tabIndex="-1"
        aria-labelledby="exportModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header d-flex justify-content-between">
              <h5 className="modal-title" id="exportModalLabel">
                تصدير الطلبات إلى Excel
              </h5>
              <button
                type="button"
                className="btn-close mx-0"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>

            <div className="modal-body">
              <form id="exportForm">
                <div className="mb-3">
                  <label className="form-label">من تاريخ</label>
                  <input type="date" className="form-control" id="dateFrom" name="date_from" />
                </div>
                <div className="mb-3">
                  <label className="form-label">إلى تاريخ</label>
                  <input type="date" className="form-control" id="dateTo" name="date_to" />
                </div>

                <div className="mb-3">
                  <label className="form-label">حالة الدفع</label>
                  <select className="form-select" id="paymentStatus" name="payment_status">
                    <option value="">الكل</option>
                    <option value="paid">مدفوع</option>
                    <option value="not_paid">غير مدفوع</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">حالة الطلب</label>
                  <select className="form-select" id="orderStatus" name="order_status">
                    <option value="">الكل</option>
                    <option value="pending">قيد الانتظار</option>
                    <option value="in_progress">قيد التنفيذ</option>
                    <option value="in_way">في الطريق</option>
                    <option value="delivered">تم التوصيل</option>
                    <option value="cancelled">ملغى</option>
                  </select>
                </div>
              </form>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                إغلاق
              </button>
              <button
                type="button"
                className="btn btn-success"
                id="exportBtn"
                onClick={handleExportOrders}
                disabled={loadingExportOrders}
              >
                {loadingExportOrders ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    جاري التصدير...
                  </>
                ) : (
                  "تصدير"
                )}
              </button>

            </div>
          </div>
        </div>
      </div>
      {/* export students modal */}
      <div
        className="modal fade"
        id="exportStudentsModal"
        tabIndex="-1"
        aria-labelledby="exportModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header d-flex justify-content-between">
              <h5 className="modal-title" id="exportModalLabel">
                تصدير الطلاب إلى Excel
              </h5>
              <button
                type="button"
                className="btn-close mx-0"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>

            <div className="modal-body">
              <form id="exportForm">
                <div className="mb-3">
                  <label className="form-label">اختر الصف</label>
                  <select
                    name="grade_id"
                    id="grade_id"
                    className="form-control"
                  >
                    <option value="">الكل</option>
                    {grades?.map((grade) => (
                      <option key={grade.id} value={grade.id}>
                        {grade.name_ar}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">اختر القسم</label>
                  <select
                    name="section_id"
                    id="section_id"
                    className="form-control"
                  >
                    <option value="">الكل</option>
                    {sections?.map((section) => (
                      <option key={section.id} value={section.id}>
                        {section.name_ar}
                      </option>
                    ))}
                  </select>
                </div>
              </form>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                إغلاق
              </button>
              <button
                type="button"
                className="btn btn-success"
                id="exportBtn"
                onClick={handleExportStudents}
                disabled={loadingExportStudents}
              >
                {loadingExportStudents ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    جاري التصدير...
                  </>
                ) : (
                  "تصدير"
                )}
              </button>

            </div>
          </div>
        </div>
      </div>
      {/* export product students modal */}
      <div
      className="modal fade"
      id="exportProductStudentsModal"
      tabIndex="-1"
      aria-labelledby="exportModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header d-flex justify-content-between">
            <h5 className="modal-title" id="exportModalLabel">
              تصدير المنتجات إلى Excel
            </h5>
            <button
              type="button"
              className="btn-close mx-0"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>

          <div className="modal-body">
            <form id="exportForm">
              <div className="mb-3">
                <label className="form-label">المنتج</label>
                  <select
                    name="product_id"
                    id="product_id"
                    className="form-control ps-5"
                    onChange={(e) => {
                      const selected = e.target.value;
                      if (selected === "") {
                        setCurrentPage(1);
                        dispatch(fetchAllProducts(1));
                      }
                    }}
                  >
                  <option value="">اختر منتج</option>
                  {allProductsLoading ? (
                    <option disabled>...جاري التحميل</option>
                  ) : (
                    allProducts?.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.title_ar}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </form>

            {/* Pagination Controls */}
            <div className="d-flex justify-content-between align-items-center mt-3">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={meta.current_page === 1}
                onClick={handlePrev}
              >
                السابق
              </button>

              <span>
                صفحة {meta.current_page} من {meta.last_page}
              </span>

              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={meta.current_page === meta.last_page}
                onClick={handleNext}
              >
                التالي
              </button>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              data-bs-dismiss="modal"
            >
              إغلاق
            </button>
             <button
              type="button"
              className="btn btn-success"
              id="exportBtn"
              onClick={handleExportProductStudent}
              disabled={loadingExportProductStudents}
            >
              {loadingExportProductStudents ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  جاري التصدير...
                </>
              ) : (
                "تصدير"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default Home;